import React, { useState, useEffect } from 'react';
import { Polyline, InfoWindow } from '@react-google-maps/api';
import { Typography } from '@mui/material';
import { useMapLegend } from '../MapLegendContext';
import { useMapClick } from '../MapClickContext';

/**
 * Returns the range object for a given value.
 * Handles both numeric and string (e.g., 'off') values.
 */
function getRangeForValue(value, ranges, discrete) {
    // Handle Weed-It Off logic explicitly
    if (String(value) === 'off') {
        return ranges.find(r => String(r.min) === 'off' && String(r.max) === 'off') || ranges[ranges.length - 1];
    }
    if (discrete) {
        // Always compare as string for discrete values
        return ranges.find(r => String(value) === String(r.min)) || ranges[ranges.length - 1];
    }
    for (const r of ranges) {
        if (typeof r.min === 'number' && typeof r.max === 'number' && value >= r.min && value < r.max) return r;
    }
    return ranges[ranges.length - 1];
}

/**
 * Builds colored segments for the polyline overlay.
 * Each segment can be solid or dashed, and can have a custom tooltip.
 */
function getColoredSegmentsForSegments(segments, valueSelector, ranges, discrete) {
  const allSegments = [];
  segments.forEach((route, routeIdx) => {
    for (let i = 0; i < route.length - 1; i++) {
      const value = valueSelector(route[i]);
      const range = getRangeForValue(value, ranges, discrete);
      allSegments.push({
        path: [route[i], route[i + 1]],
        color: range?.color,
        value,
        start: route[i],
        end: route[i + 1],
        dashed: range?.dashed || false,
        tooltip: range?.value === 'Weed-It Off' ? 'Weed-It Off' : undefined,
        eventDateUtc: route[i].eventDateUtc,
        routeIdx
      });
    }
  });
  return allSegments;
}

// Format a UTC date string as local time (HH:MM:SS)
function formatLocalTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
  return d.toLocaleTimeString();
}

// Helper to get midpoint between two lat/lng points
function getMidpoint(p1, p2) {
  return {
    lat: (p1.lat + p2.lat) / 2,
    lng: (p1.lng + p2.lng) / 2,
  };
}

export default function SegmentedPolylineOverlay({
  segments, // array of arrays of points
  valueSelector,
  ranges,
  legendTitle = 'Legend',
  legendFormatter = r => `${r.min} - ${r.max}`,
  units = '',
  discrete = false,
}) {
  const [hovered, setHovered] = useState(null);
  const [selectedLegendValue, setSelectedLegendValue] = useState(null);
  const legendContext = useMapLegend();
  const mapClick = useMapClick();

  useEffect(() => {
    if (!legendContext) return;
    legendContext.setLegend({ ranges, legendTitle, legendFormatter, discrete, selectedLegendValue, setSelectedLegendValue });
    return () => legendContext.setLegend(null);
  }, [ranges, legendTitle, legendFormatter, discrete, selectedLegendValue]);

  const coloredSegments = React.useMemo(
    () => getColoredSegmentsForSegments(segments, valueSelector, ranges, discrete),
    [segments, valueSelector, ranges, discrete]
  );

  // Highlight logic
  const isSegmentHighlighted = seg => {
    if (selectedLegendValue == null) return false;
    if (discrete) return String(seg.value) === String(selectedLegendValue);
    return seg.value >= selectedLegendValue.min && seg.value < selectedLegendValue.max;
  };

  return (
    <>
      {coloredSegments.map((seg, idx) => (
        <Polyline
          key={idx}
          path={seg.path}
          options={{
            strokeColor: seg.color,
            strokeOpacity: seg.dashed ? 0 : 0.9,
            strokeWeight: isSegmentHighlighted(seg) ? 10 : 5,
            zIndex: isSegmentHighlighted(seg) ? 10 : 2,
            ...(seg.dashed
              ? {
                  icons: [
                    {
                      icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 1,
                        scale: 4,
                      },
                      offset: '0',
                      repeat: '24px',
                    },
                  ],
                }
              : {}),
          }}
          onMouseOver={() => setHovered({ idx, seg })}
          onMouseOut={() => setHovered(null)}
          onClick={(e) => { if (mapClick && e.latLng) mapClick.setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
        />
      ))}
      {hovered && (
        <InfoWindow
          position={getMidpoint(hovered.seg.start, hovered.seg.end)}
          options={{ disableAutoPan: true, closeBoxURL: '' }}
        >
          <div style={{ textAlign: 'center' }}>
            <Typography variant="body1">
              {hovered.seg.tooltip ||
                (typeof hovered.seg.value === 'number'
                  ? hovered.seg.value.toFixed(1)
                  : hovered.seg.value)}
              {units && !hovered.seg.tooltip && ` ${units}`}
            </Typography>
            {hovered.seg.eventDateUtc && (
              <Typography variant="body2" sx={{ display: 'block' }}>
                {formatLocalTime(hovered.seg.eventDateUtc)}
              </Typography>
            )}
          </div>
        </InfoWindow>
      )}
      <style>
        {`
          .gm-ui-hover-effect {
            display: none !important;
          }
        `}
      </style>
    </>
  );
}